/**
 * AOI 配送时间 - 前端结构化 schema + parse / serialize 工具。
 *
 * <p>后端 `biz_aoi_attribute.delivery_time` 字段仍然是 JSON 字符串, 这里只在前端做结构化展示 / 编辑。
 *
 * <p>Schema (v1):
 * <pre>
 * {
 *   "v": 1,
 *   "workday":  { "ranges": [{"start": "HH:mm", "end": "HH:mm"}], "remark": null | string },
 *   "restday":  { ... },
 *   "holiday":  { ... },
 *   "other":    [ { "name": "复活节", "ranges": [...], "remark": null | string }, ... ]
 * }
 * </pre>
 *
 * <p>设计要点:
 * <ul>
 *   <li>固定 3 个分类: 工作日 / 休息日 / 节假日 (workday / restday / holiday);</li>
 *   <li>`other` 是 list, 每个条目用户自己命名 (复活节 / 黑五 / ...);</li>
 *   <li>时段 `end < start` 表示跨日 (例如 22:00 → 02:00); `00:00 - 24:00` 表示全天;</li>
 *   <li>时区: 不存, 默认解读为 AOI 当地时间 (zipcode 决定地理位置, 进而决定时区);</li>
 *   <li>每个 list 上限 {@link MAX_LIST_SIZE} 条。</li>
 * </ul>
 */

import { MAX_LIST_SIZE } from '../constants';

/** 时段 (开始 - 结束, 24h, AOI 当地时间)。 */
export interface DeliveryTimeRange {
    /** "HH:mm", 24h 格式。 */
    start: string;
    /** "HH:mm". end < start 表示跨日; "24:00" 表示一天结束。 */
    end: string;
}

/** 固定分类 (workday / restday / holiday) 的结构。 */
export interface DeliveryTimeCategory {
    ranges: DeliveryTimeRange[];
    remark: string | null;
}

/** `other` 列表里的单条 - 用户命名 + 时段 + 备注。 */
export interface DeliveryTimeOtherEntry {
    /** 用户填的名称, "复活节" / "黑五" / ... 不能为空。 */
    name: string;
    ranges: DeliveryTimeRange[];
    remark: string | null;
}

/** 配送时间的完整结构化对象。 */
export interface DeliveryTime {
    v: number;
    workday: DeliveryTimeCategory;
    restday: DeliveryTimeCategory;
    holiday: DeliveryTimeCategory;
    other:   DeliveryTimeOtherEntry[];
}

/** 当前 schema 版本。 */
export const DELIVERY_TIME_SCHEMA_VERSION = 1;

/** 空的默认值 (新建 AOI / 没数据时使用)。 */
export function emptyDeliveryTime(): DeliveryTime {
    return {
        v: DELIVERY_TIME_SCHEMA_VERSION,
        workday: emptyCategory(),
        restday: emptyCategory(),
        holiday: emptyCategory(),
        other: [],
    };
}

export function emptyCategory(): DeliveryTimeCategory {
    return { ranges: [], remark: null };
}

export function emptyOtherEntry(): DeliveryTimeOtherEntry {
    return { name: '', ranges: [], remark: null };
}

/* ====================== Parse ====================== */

/**
 * 把后端的 JSON 字符串解析成 {@link DeliveryTime}。
 *
 * <p>不做老 schema 兼容 (用户确认不需要), 严格按 v1 解析:
 * <ul>
 *   <li>raw 为空 / 不是合法 JSON → 返回空结构;</li>
 *   <li>缺字段或字段类型不对 → 该字段用空默认值。</li>
 * </ul>
 */
export function parseDeliveryTime(raw: string | null | undefined): DeliveryTime {
    if (!raw || !raw.trim()) return emptyDeliveryTime();
    let obj: unknown;
    try {
        obj = JSON.parse(raw);
    } catch {
        return emptyDeliveryTime();
    }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return emptyDeliveryTime();

    const o = obj as Record<string, unknown>;
    const result = emptyDeliveryTime();
    result.v = typeof o.v === 'number' ? o.v : DELIVERY_TIME_SCHEMA_VERSION;
    result.workday = normalizeCategory(o.workday);
    result.restday = normalizeCategory(o.restday);
    result.holiday = normalizeCategory(o.holiday);
    if (Array.isArray(o.other)) {
        result.other = o.other
            .map(normalizeOtherEntry)
            .filter((e): e is DeliveryTimeOtherEntry => e !== null);
    }
    return result;
}

function normalizeCategory(input: unknown): DeliveryTimeCategory {
    if (!input || typeof input !== 'object') return emptyCategory();
    const o = input as Record<string, unknown>;
    return {
        ranges: normalizeRanges(o.ranges),
        remark: typeof o.remark === 'string' ? o.remark : null,
    };
}

function normalizeOtherEntry(input: unknown): DeliveryTimeOtherEntry | null {
    if (!input || typeof input !== 'object') return null;
    const o = input as Record<string, unknown>;
    return {
        name: typeof o.name === 'string' ? o.name : '',
        ranges: normalizeRanges(o.ranges),
        remark: typeof o.remark === 'string' ? o.remark : null,
    };
}

function normalizeRanges(input: unknown): DeliveryTimeRange[] {
    if (!Array.isArray(input)) return [];
    return input
        .map((r): DeliveryTimeRange | null => {
            if (!r || typeof r !== 'object') return null;
            const o = r as Record<string, unknown>;
            const start = typeof o.start === 'string' ? o.start : '';
            const end = typeof o.end === 'string' ? o.end : '';
            if (!start || !end) return null;
            return { start, end };
        })
        .filter((r): r is DeliveryTimeRange => r !== null);
}

/* ====================== Serialize ====================== */

/**
 * 把 {@link DeliveryTime} 序列化成给后端的 JSON 字符串。
 *
 * <p>全部为空 (3 个分类都没 ranges 也没 remark, other 是空数组) 时返回 null,
 * 让后端 delivery_time 字段保持 NULL, 不污染 DB。
 */
export function serializeDeliveryTime(d: DeliveryTime): string | null {
    if (isEmptyDeliveryTime(d)) return null;
    return JSON.stringify({
        v: DELIVERY_TIME_SCHEMA_VERSION,
        workday: d.workday,
        restday: d.restday,
        holiday: d.holiday,
        other: d.other,
    });
}

export function isEmptyDeliveryTime(d: DeliveryTime): boolean {
    return isEmptyCategory(d.workday)
        && isEmptyCategory(d.restday)
        && isEmptyCategory(d.holiday)
        && d.other.length === 0;
}

function isEmptyCategory(c: DeliveryTimeCategory): boolean {
    return c.ranges.length === 0 && !c.remark;
}

/* ====================== 校验 ====================== */

/** 校验结果, ok=false 时 message 给出第一条错误用于 UI 提示。 */
export interface DeliveryTimeValidation {
    ok: boolean;
    message?: string;
}

/**
 * 统计配送时间里全部时段总数:
 * workday/restday/holiday 各自的 ranges 长度 + 每个 other 条目里 ranges 长度的总和。
 *
 * <p>50 的上限是针对这个总数, 不是每个 category 各自的, 也不是 other 数组的长度。
 */
export function countDeliveryTimeRanges(d: DeliveryTime): number {
    return d.workday.ranges.length
        + d.restday.ranges.length
        + d.holiday.ranges.length
        + d.other.reduce((sum, e) => sum + e.ranges.length, 0);
}

/**
 * 保存前校验. 软错误 (重叠 / end=start 等) 不在这里查, 由 UI 软提示;
 * 这里只查会导致后端写入失败的硬错误:
 * <ul>
 *   <li>所有 ranges 总数 ≤ {@link MAX_LIST_SIZE} (整个 deliveryTime 的总上限);</li>
 *   <li>other 条目的 name 不能为空;</li>
 *   <li>每个 range 的 start / end 必须是合法 HH:mm。</li>
 * </ul>
 */
export function validateDeliveryTime(d: DeliveryTime): DeliveryTimeValidation {
    const total = countDeliveryTimeRanges(d);
    if (total > MAX_LIST_SIZE) {
        return { ok: false, message: `配送时间总时段数 ${total} 超过上限 ${MAX_LIST_SIZE}` };
    }
    for (const [key, c] of [
        ['工作日', d.workday],
        ['休息日', d.restday],
        ['节假日', d.holiday],
    ] as const) {
        const r = validateRangeFormats(c.ranges, key);
        if (!r.ok) return r;
    }
    for (let i = 0; i < d.other.length; i++) {
        const e = d.other[i];
        if (!e.name.trim()) {
            return { ok: false, message: `「其他」第 ${i + 1} 条名称不能为空` };
        }
        const r = validateRangeFormats(e.ranges, `「其他 - ${e.name}」`);
        if (!r.ok) return r;
    }
    return { ok: true };
}

/** 单独验单个 ranges 数组里每个 range 的 HH:mm 格式 (不查长度上限, 总数由 validateDeliveryTime 兜底)。 */
function validateRangeFormats(ranges: DeliveryTimeRange[], label: string): DeliveryTimeValidation {
    for (let i = 0; i < ranges.length; i++) {
        const { start, end } = ranges[i];
        if (!isValidHHmm(start) || !isValidHHmm(end)) {
            return { ok: false, message: `${label} 第 ${i + 1} 个时段格式错误` };
        }
    }
    return { ok: true };
}

/** HH:mm (允许 24:00 表示一天结束)。 */
function isValidHHmm(s: string): boolean {
    if (!/^\d{2}:\d{2}$/.test(s)) return false;
    const [hh, mm] = s.split(':').map(Number);
    if (mm < 0 || mm > 59) return false;
    if (hh < 0 || hh > 24) return false;
    if (hh === 24 && mm !== 0) return false;  // 只允许 24:00, 不允许 24:01
    return true;
}

/* ====================== 显示工具 ====================== */

/** 把单个 range 转成显示字符串, 含跨日 / 全天 标记。 */
export function formatRange(r: DeliveryTimeRange): string {
    if (r.start === '00:00' && r.end === '24:00') return '全天 (00:00 - 24:00)';
    const crossesDay = r.end < r.start;
    return crossesDay ? `${r.start} - ${r.end} (次日)` : `${r.start} - ${r.end}`;
}
